// src/api.ts
import axios from "axios";

const API_BASE = (
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === "production"
    ? "https://napkin-llsqz.ondigitalocean.app"
    : "http://localhost:5050")
).trim();

export const api = axios.create({
  baseURL: API_BASE.replace(/\/+$/, ""), // no trailing slash
});

// --- Token helpers (localStorage) ---
const ACCESS_TOKEN_KEY = "napkin_access_token";
const REFRESH_TOKEN_KEY = "napkin_refresh_token";

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getAccessToken = (): string | null =>
  localStorage.getItem(ACCESS_TOKEN_KEY);

export const getRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// --- Request interceptor: attach Authorization header ---
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Optional: wire a callback for unauthorized ---
let onUnauthorized: null | (() => void) = null;
export const setOnUnauthorized = (fn: () => void) => { onUnauthorized = fn; };

const isPublicSigningUrl = (url?: string) =>
  !!url &&
  (url.includes("/api/recipient/") ||
    url.includes("/api/open-envelope-recipient/") ||
    /\/api\/envelope\/[^/]+\/pdf/.test(url));

// --- Silent refresh on 401/403 ---
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (v?: unknown) => void;
  reject: (e: unknown) => void;
}> = [];

const drainQueue = (err: unknown | null) => {
  refreshQueue.forEach((p) => (err ? p.reject(err) : p.resolve()));
  refreshQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const original = error.config;

    // Public signing endpoints: signers have no account, so a 401/403 here is a
    // real answer (voided, expired, not assignee), not an expired session.
    if (isPublicSigningUrl(original?.url)) {
      return Promise.reject(error);
    }

    // Only attempt silent refresh for 401/403 on non-refresh, non-retried requests
    if (
      (status === 401 || status === 403) &&
      !original._retry &&
      !original.url?.includes("/api/refresh") &&
      !original.url?.includes("/api/login") &&
      !original.url?.includes("/api/signup") &&
      !original.url?.includes("/api/forgot-password") &&
      !original.url?.includes("/api/reset-password") &&
      !original.url?.includes("/api/logout")
    ) {
      // If a refresh is already in flight, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(original));
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) throw new Error("No refresh token");

        const res = await api.post("/api/refresh", { refreshToken });
        const { accessToken: newAccess, refreshToken: newRefresh } = res.data;
        setTokens(newAccess, newRefresh);

        drainQueue(null);
        // Retry the original request with fresh token
        return api(original);
      } catch (refreshErr) {
        drainQueue(refreshErr);
        // Refresh failed — session is truly dead
        clearTokens();
        try {
          sessionStorage.clear();
        } catch {}
        if (onUnauthorized) onUnauthorized();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // For refresh endpoint failures or already-retried requests, nuke auth
    if (status === 401 || status === 403) {
      clearTokens();
      try {
        sessionStorage.clear();
      } catch {}
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(error);
  }
);
